export const interiorDesignersData = {
  industry: "Interior Designers",
  slug: "interior-designers",
  
  seo: {
    title: "Intake Forms for Interior Designers | HeyIntake",
    description: "Qualify design clients and capture project requirements with AI-powered intake forms. Know the budget, style preferences, timeline, and scope before the first consultation.",
    keywords: [
      "interior design intake form",
      "design client questionnaire",
      "interior designer project form",
      "home design consultation form",
      "design client onboarding",
      "interior design lead form"
    ]
  },

  heroTitle: "Intake Forms for Interior Designers",
  heroSubtitle: "Capture budget, style, timeline, and scope before your first consultation. Qualify clients, not just inquiries.",
  heroDescription: "Not every inquiry is your dream project. HeyIntake helps you collect budget range, room scope, style inspiration, and timeline so you can identify ideal clients and politely pass on mismatches.",
  
  whyAIScoring: {
    title: "Why Interior Designers Need Smart Client Intake",
    intro: "Design consultations are valuable — both your time and your creative energy. Spending an hour with someone whose budget is 10% of what the project requires isn't just frustrating, it's unprofitable.",
    paragraphs: [
      "A simple 'I need help with my living room' tells you nothing. Is it a furniture arrangement consultation or a full gut renovation? Is their budget $5,000 or $50,000? Do they want mid-century modern or coastal farmhouse? Without context, you're walking into consultations blind.",
      "HeyIntake captures the details that matter before you invest time. Project scope, room dimensions, style preferences (with Pinterest boards), budget range, timeline, and decision-maker involvement. Our AI identifies which inquiries match your minimum project size and design aesthetic.",
      "For residential designers, commercial interiors, and e-design businesses, this means consultations with qualified clients, projects that fit your portfolio goals, and fewer awkward budget conversations."
    ]
  },

  painPoints: [
    {
      title: "Budget Mismatches",
      description: "Discovering after an hour-long consultation that their $5K budget won't cover your design fee, let alone furniture."
    },
    {
      title: "Vague Project Scope",
      description: "'Help me with my house' could mean one room refresh or whole-home renovation — you need specifics to quote."
    },
    {
      title: "Style Miscommunication",
      description: "Their 'modern' and your 'modern' might be completely different aesthetics."
    },
    {
      title: "Timeline Unreality",
      description: "Clients expecting a complete design in 2 weeks when you're booked 3 months out."
    },
    {
      title: "Decision-Maker Absence",
      description: "Designing for one spouse, then discovering the other has veto power and different taste."
    },
    {
      title: "Scope Creep Setup",
      description: "No documented project boundaries means constant 'while you're at it' additions."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Property",
      fields: [
        "Your name",
        "Phone and email",
        "Property address",
        "Property type (home, condo, office)",
        "Own or rent?"
      ]
    },
    {
      category: "Project Scope",
      fields: [
        "Which rooms need design help?",
        "Full design or specific elements?",
        "Are you renovating or refreshing?",
        "Approximate square footage",
        "Any structural changes planned?"
      ]
    },
    {
      category: "Style & Vision",
      fields: [
        "Describe your style in 3 words",
        "Pinterest board or inspiration images",
        "Colors you love/hate",
        "Existing pieces to keep",
        "Lifestyle needs (kids, pets, entertaining)"
      ]
    },
    {
      category: "Budget & Timeline",
      fields: [
        "Total project budget range",
        "Is budget flexible?",
        "Ideal completion date",
        "Decision-makers involved",
        "Have you worked with a designer before?"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Interior Design Intake Form",
    description: "Here's what a qualifying design intake looks like:",
    fields: [
      { label: "What type of project is this?", type: "select", options: ["Single room refresh", "Multiple rooms", "Whole home design", "New construction", "Commercial space"] },
      { label: "Which rooms need design help?", type: "textarea" },
      { label: "Total project budget (including furniture)", type: "select", options: ["$5K-$15K", "$15K-$30K", "$30K-$75K", "$75K-$150K", "$150K+"] },
      { label: "Describe your style", type: "select", options: ["Modern/Contemporary", "Traditional", "Transitional", "Mid-Century", "Coastal", "Farmhouse", "Eclectic", "Not sure"] },
      { label: "Share Pinterest board or inspiration images", type: "text", placeholder: "Paste link here" },
      { label: "Ideal project completion", type: "select", options: ["ASAP", "1-3 months", "3-6 months", "6-12 months", "Flexible"] },
      { label: "Who will be making design decisions?", type: "select", options: ["Just me", "Me and partner", "Family committee", "Business stakeholders"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Designers",
    subtitle: "From inquiry to qualified consultation",
    steps: [
      {
        title: "Client Shares Their Vision",
        description: "Potential clients complete your intake with scope, budget, style preferences, and inspiration."
      },
      {
        title: "AI Qualifies & Matches",
        description: "Our AI scores budget fit, project size, and alignment with your design services."
      },
      {
        title: "Consult with Context",
        description: "Enter consultations knowing the scope, budget, and aesthetic — ready to present relevant ideas."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Qualification for Designers",
    description: "HeyIntake's AI helps you identify dream projects and politely decline budget mismatches before investing consultation time.",
    factors: [
      "Budget alignment with project scope",
      "Style fit with your portfolio aesthetic",
      "Timeline feasibility",
      "Decision-maker involvement",
      "Project size vs. your minimum"
    ],
    exampleLead: {
      name: "Michelle & David Park",
      context: "Whole Home • $125K Budget • Modern Transitional",
      summary: "New home, 3,200 sq ft, seeking whole-home design. $125K budget for design + furnishings. Modern transitional style, Pinterest board attached. Both decision-makers available. 6-month timeline.",
      score: 94
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Screen Budget Fit Instantly",
      description: "Know if their budget matches the project scope before booking a consultation."
    },
    {
      icon: "file",
      title: "Collect Inspiration Early",
      description: "Have clients share Pinterest boards and photos before you meet."
    },
    {
      icon: "users",
      title: "Identify Decision Makers",
      description: "Know who has approval authority before designing for the wrong person."
    },
    {
      icon: "clock",
      title: "Set Timeline Expectations",
      description: "Understand their deadline vs. your availability upfront."
    },
    {
      icon: "zap",
      title: "Automate Follow-Up",
      description: "Send proposals, contracts, and project timelines automatically."
    },
    {
      icon: "chart",
      title: "Track Project Pipeline",
      description: "See all inquiries from initial contact through project completion."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" },
    { name: "Intake Forms for Real Estate Agents", path: "/intake-form-for-real-estate-agents" },
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" },
    { name: "Intake Forms for Photographers", path: "/intake-form-for-photographers" }
  ],

  ctaText: "Ready to Attract Your Dream Projects?"
};
